import { trackRequest, outcomeForStatus } from '@/features/connection';
import { refreshesOn401 } from '@/lib/auth-paths';

export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

export class ApiError extends Error {
  status: number;
  data: unknown;
  constructor(message: string, status: number, data: unknown) {
    super(message);
    this.status = status;
    this.data = data;
  }
}

// ---- Session expiry handling ------------------------------------------------
// When the access token AND the refresh token are both dead, we used to throw
// raw 401s into every component. Instead: clear the persisted user once and
// send the browser to /login?expired=1 (which shows a friendly banner).
let expiryHandled = false;
function handleSessionExpired() {
  if (expiryHandled) return;
  expiryHandled = true;
  try { localStorage.removeItem('bmail-auth'); } catch { /* storage blocked */ }
  if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
    window.location.assign('/login?expired=1');
  }
}

// ---- Refresh mutex ----------------------------------------------------------
// Parallel 401s must share ONE /auth/refresh call. The backend rotates the
// refresh token, so two concurrent refreshes race and the loser deletes the
// winner's session. Single-flight promise fixes that.
let refreshInFlight: Promise<boolean> | null = null;

function silentRefresh(): Promise<boolean> {
  if (!refreshInFlight) {
    refreshInFlight = (async () => {
      try {
        const r = await fetch(`${API_BASE_URL}/auth/refresh`, {
          method: 'POST',
          credentials: 'include',
          headers: { Accept: 'application/json' }
        });
        return r.ok;
      } catch {
        return false; // offline / network error
      } finally {
        // Allow a future refresh cycle after this one settles.
        setTimeout(() => { refreshInFlight = null; }, 0);
      }
    })();
  }
  return refreshInFlight;
}

async function request<T = unknown>(path: string, options: RequestInit = {}, _retried = false): Promise<T> {
  // C1: token lives in an httpOnly cookie set by the backend — JS can't read it.
  // We send `credentials: 'include'` so the browser attaches the cookie automatically.
  const url = path.startsWith('http') ? path : `${API_BASE_URL}${path}`;
  const headers = new Headers(options.headers);
  headers.set('Accept', 'application/json');
  if (options.body && typeof options.body === 'string' && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  // Connection feedback: only reads are timed — a sync or send POST is allowed to take a while.
  const settle = trackRequest({ timed: (options.method ?? 'GET') === 'GET' });
  let res: Response;
  try {
    res = await fetch(url, { ...options, headers, credentials: 'include' });
  } catch (err) {
    settle('network-error');
    throw err;
  }
  settle(outcomeForStatus(res.status));

  // Persistent login: on a 401, try one shared silent refresh and replay the
  // request so the user stays logged in on web and iPhone. If the refresh also
  // fails, it's a real expiry — route to login with a friendly notice instead
  // of spraying errors across every fetch in the app.
  if (res.status === 401 && !_retried && refreshesOn401(path)) {
    const refreshed = await silentRefresh();
    if (refreshed) return request<T>(path, options, true);
    handleSessionExpired();
  }

  const text = await res.text();
  const data = text ? JSON.parse(text) : null;

  if (!res.ok) {
    throw new ApiError(data?.error || `HTTP ${res.status}`, res.status, data);
  }
  return data as T;
}

/**
 * POST with real upload progress.
 *
 * 🔴 `fetch` CANNOT report upload progress — there are no events for the request body, only for the
 * response. XHR is the only way to get `upload.onprogress` in a browser today, so this one call
 * uses it. Everything else should keep using `api.post`.
 *
 * 🚩 It deliberately repeats `request()`'s 401 handling rather than skipping it: an image upload is
 * the longest request in the app, so it is the MOST likely to meet an access token that expired
 * while it was in flight. Without the replay, a 24 MB upload would simply fail at the end.
 *
 * `onProgress` is called with 0..1 for bytes SENT. It cannot cover the server's own work — the
 * caller shows that as an indeterminate phase.
 */
export function postWithUploadProgress<T = unknown>(
  path: string,
  body: unknown,
  onProgress?: (fraction: number) => void,
  _retried = false,
): Promise<T> {
  const url = path.startsWith('http') ? path : `${API_BASE_URL}${path}`;
  const payload = JSON.stringify(body);

  return new Promise<T>((resolve, reject) => {
    // Untimed: a big upload is meant to be slow; only its failure says anything about the connection.
    const settle = trackRequest({ timed: false });
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url, true);
    xhr.withCredentials = true;                       // the session cookie is httpOnly
    xhr.setRequestHeader('Accept', 'application/json');
    xhr.setRequestHeader('Content-Type', 'application/json');

    xhr.upload.onprogress = (e) => {
      // 🚩 `lengthComputable` is false on some proxies; report nothing rather than a fake number.
      if (e.lengthComputable && e.total > 0) onProgress?.(Math.min(1, e.loaded / e.total));
    };
    // The last progress event can arrive slightly before the body is fully flushed; settle at 1.
    xhr.upload.onload = () => onProgress?.(1);

    xhr.onload = async () => {
      settle(outcomeForStatus(xhr.status));
      if (xhr.status === 401 && !_retried && refreshesOn401(path)) {
        const refreshed = await silentRefresh();
        if (refreshed) {
          resolve(await postWithUploadProgress<T>(path, body, onProgress, true));
          return;
        }
        handleSessionExpired();
      }
      let data: unknown = null;
      try { data = xhr.responseText ? JSON.parse(xhr.responseText) : null; }
      catch { /* a non-JSON body (a proxy error page) stays null and is reported by status */ }
      if (xhr.status >= 200 && xhr.status < 300) return resolve(data as T);
      const msg = (data as { error?: string } | null)?.error || `HTTP ${xhr.status}`;
      reject(new ApiError(msg, xhr.status, data));
    };
    xhr.onerror = () => { settle('network-error'); reject(new ApiError('Network error', 0, null)); };
    xhr.ontimeout = () => { settle('network-error'); reject(new ApiError('The upload timed out', 0, null)); };
    xhr.onabort = () => { settle('cancelled'); reject(new ApiError('Upload cancelled', 0, null)); };

    xhr.send(payload);
  });
}

export const api = {
  get: <T = unknown>(path: string) => request<T>(path, { method: 'GET' }),
  post: <T = unknown>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body: JSON.stringify(body) }),
  patch: <T = unknown>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body: JSON.stringify(body) }),
  delete: <T = unknown>(path: string) => request<T>(path, { method: 'DELETE' })
};
